import type { PaginationEventHandlersParams } from '@ksyos/design-system/dist/components/pagination';
import { Box, Card, FormGroupContext, Loader, Pagination } from '@ksyos/design-system';
import { useNavigate, useSearchParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import type { FundingErrorInformation } from '@common/enums/fundingErrorInformation';
import type { FundingProcess, GetFundingProcessesResponseBody } from '@common/interfaces/api/fundingProcessApi';
import type { FinancingFlexSearchFilterValidated } from '@common/validation/financingFlex';
import { ManagerAuthorization } from '@common/enums';
import { formatString } from '@common/utils/formatString';
import {
    financingFlexSearchFilterSchema,
    financingFlexSearchFilterSchemaKeys,
    financingFlexSearchFilterValidationSchema,
} from '@common/validation/financingFlex';
import { ConfirmationModal } from '@frontend/components/elements/modals/ConfirmationModal';
import { NoFinancingSearchResults } from '@frontend/components/pages/finance/NoFinancingSearchResults';
import { getSelectedLanguage } from '@frontend/translate';
import { assertHasAuthorization, useAbortableFetch, validate } from '@frontend/utils';
import { formatDateIntoString } from '@frontend/utils/formatDateIntoString';
import type { FinancingFlexSearchInputFilters } from './financingFlexSearchInputFilters';
import { createUrlWithFilterParams, validateLocationQuery } from '../../financeHelperFunctions';
import { OtherFundingErrorInformationModal } from '../OtherFundingErrorInformationModal';
import { constructSearchForFilter, isAtLeastOneFilterSelected } from '../searchHelperFunctions';
import { FinancingFlexProcessesFooterButtons } from './FinancingFlexProcessesFooterButtons';
import { FinancingFlexSearchFilters } from './FinancingFlexSearchFilters';
import { FinancingFlexTable } from './financingFlexTable/FinancingFlexTable';

export function FinancingFlex() {
    assertHasAuthorization(ManagerAuthorization.financeFunding);
    const language = getSelectedLanguage();
    const navigate = useNavigate();
    const abortableFetch = useAbortableFetch();
    const [searchParams] = useSearchParams();
    const [isReprocessing, setIsReprocessing] = useState(false);
    const [financingSearchFilters, setFinancingSearchFilters] = useState<FinancingFlexSearchInputFilters>({});
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [items, setItems] = useState<FundingProcess[]>([]);
    const [allItemsSelected, setAllItemsSelected] = useState(false);
    const [total, setTotal] = useState(0);
    const [isSearching, setIsSearching] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [selectedRows, setSelectedRows] = useState<FundingProcess[]>([]);
    const [showFinancingReprocessModal, setShowFinancingReprocessModal] = useState(false);
    const [selectedRowProcessNumberWithOtherFundingError, setSelectedRowProcessNumberWithOtherFundingError] =
        useState<number>();

    const isSearchButtonDisabled = isSearching || isReprocessing || !isAtLeastOneFilterSelected(financingSearchFilters);

    useEffect(() => {
        const queryParameters = validateLocationQuery(searchParams, financingFlexSearchFilterSchema);

        setFinancingSearchFilters({ ...financingSearchFilters, ...queryParameters });
    }, []);

    useEffect(() => {
        setAllItemsSelected(items.length > 0 && selectedRows.length === items.length);
    }, [selectedRows.length]);

    useEffect(() => {
        if (!isInitialLoad) {
            search(page);
        }
    }, [page, pageSize]);

    useEffect(() => {
        clearSearchResults();
    }, [financingSearchFilters]);

    return (
        <>
            <ConfirmationModal
                handleNo={handleDontReprocess}
                handleYes={handleReprocess}
                isOpen={showFinancingReprocessModal}
                question={formatString(language.finance.confirmationMessageReprocess, [selectedRows.length.toString()])}
                title={language.common.notice}
            />

            <OtherFundingErrorInformationModal
                onClose={() => setSelectedRowProcessNumberWithOtherFundingError(undefined)}
                onUpdateFundingErrorInformation={async (customFundingError) => {
                    if (!customFundingError || !selectedRowProcessNumberWithOtherFundingError) {
                        return;
                    }
                    updateErrorInformation(selectedRowProcessNumberWithOtherFundingError, customFundingError);
                }}
                showOtherFundingErrorInformationModal={Boolean(selectedRowProcessNumberWithOtherFundingError)}
            />

            <FormGroupContext.Provider
                value={{
                    size: 'large',
                    variant: 'outline',
                }}
            >
                <FinancingFlexSearchFilters
                    disableSearch={isSearchButtonDisabled}
                    financingSearchFilters={financingSearchFilters}
                    onChangeFinancingSearchFilters={setFinancingSearchFilters}
                    onSearch={handleSearch}
                />
            </FormGroupContext.Provider>

            {!isSearching && (
                <Box>
                    <NoFinancingSearchResults
                        isInitialLoad={isInitialLoad}
                        numberOfItems={items.length}
                    />
                </Box>
            )}

            {!isInitialLoad && !isSearching && items.length > 0 && (
                <>
                    <Card>
                        <Card.Header title={language.common.searchResults} />

                        {isSearching && (
                            <Box
                                alignContent="center"
                                alignItems="center"
                                display="flex"
                                justifyContent="center"
                            >
                                <Loader />
                            </Box>
                        )}

                        {
                            <FinancingFlexTable
                                allItemsSelected={allItemsSelected}
                                items={items}
                                onChangeFundingErrorInformation={handleChangeFundingErrorInformation}
                                onSelectAll={handleSelectAll}
                                onSelectRow={handleSelectRow}
                                selectedRows={selectedRows}
                            />
                        }
                        <Card.Footer>
                            <Pagination
                                onChange={handlePaginationChange}
                                page={page}
                                pageSize={pageSize}
                                totalItems={total}
                            />
                        </Card.Footer>
                    </Card>

                    <FinancingFlexProcessesFooterButtons
                        isReprocessButtonDisabled={selectedRows.length === 0 || isReprocessing}
                        onReprocess={handleProcessFunding}
                    />
                </>
            )}
        </>
    );

    function handleSelectRow(fundingProcess: FundingProcess) {
        const isSelected = selectedRows.includes(fundingProcess);
        if (!isSelected) {
            return setSelectedRows([...selectedRows, fundingProcess]);
        }

        setSelectedRows(selectedRows.filter((row) => row !== fundingProcess));
    }

    function handleChangeFundingErrorInformation(
        processNumber: number,
        fundingErrorInformation?: FundingErrorInformation
    ) {
        if (fundingErrorInformation === 'other' && processNumber) {
            setSelectedRowProcessNumberWithOtherFundingError(processNumber);

            return;
        }

        updateErrorInformation(processNumber, fundingErrorInformation);
    }

    function handleSelectAll(value: boolean) {
        setSelectedRows(value ? items : []);

        setAllItemsSelected(value);
    }

    function handleProcessFunding() {
        setIsReprocessing(true);
        setShowFinancingReprocessModal(true);
    }

    async function updateErrorInformation(processNumber: number, errorInformation?: FundingErrorInformation) {
        if (!items.length) {
            return;
        }

        await abortableFetch.fetchJson(
            `/api/finance/fundingResults/${processNumber}/updateFundingErrorInformation`,
            'POST',
            {
                fundingErrorInformation: errorInformation ? errorInformation : null,
            }
        );

        // Optimistic update without fetching the data again
        const newItems = items.map((item) =>
            item.processNumber === processNumber
                ? {
                      ...item,
                      fundingResult: item.fundingResult
                          ? {
                                ...item.fundingResult,
                                fundingErrorInformation: errorInformation ?? null,
                            }
                          : null,
                  }
                : item
        );

        setItems(newItems);
    }

    function handleDontReprocess() {
        setShowFinancingReprocessModal(false);
        setIsReprocessing(false);
    }

    async function handleReprocess() {
        setShowFinancingReprocessModal(false);

        const requestParameters = allItemsSelected
            ? getFiltersFlex(financingSearchFilters)
            : { processNumbers: selectedRows.map((item) => item.processNumber) };

        await abortableFetch.fetchJson('/api/finance/fundingProcesses/reprocess', 'POST', requestParameters);
        setIsReprocessing(false);
        setAllItemsSelected(false);
        setSelectedRows([]);
    }

    async function handlePaginationChange(params: PaginationEventHandlersParams) {
        setPage(params.page);
        setPageSize(params.pageSize);
        setSelectedRows([]);
    }

    function clearSearchResults() {
        setItems([]);
        setSelectedRows([]);
        setIsInitialLoad(false);
        setPage(1);
        setTotal(0);
    }

    async function handleSearch() {
        clearSearchResults();
        await search(1);
    }

    async function search(searchPage: number) {
        updateBrowserUrl();

        const requestSearchParams = {
            ...getFiltersFlex(financingSearchFilters),
            paging: {
                limit: pageSize,
                offset: pageSize * (searchPage - 1),
            },
        };

        setIsSearching(true);

        const result: GetFundingProcessesResponseBody = (
            await abortableFetch.fetchJson('/api/finance/fundingProcesses', 'POST', requestSearchParams)
        ).json;

        setItems(result.items);
        setTotal(result.total);
        setIsSearching(false);
    }

    function getFiltersFlex(filters: FinancingFlexSearchInputFilters): FinancingFlexSearchFilterValidated {
        const { endDate, searchText, searchField, startDate } = financingSearchFilters;

        const formattedSearchFilters = {
            ...filters,
            ...constructSearchForFilter(searchText, searchField),
            endDate: formatDateIntoString(endDate) ?? undefined,
            startDate: formatDateIntoString(startDate) ?? undefined,
        };

        const { hasErrors, item } = validate(financingFlexSearchFilterValidationSchema, formattedSearchFilters);

        if (hasErrors) {
            throw new Error('The search filter contains invalid values.');
        }

        return item;
    }

    function updateBrowserUrl() {
        const url = createUrlWithFilterParams(
            Object.keys(financingFlexSearchFilterSchemaKeys),
            financingSearchFilters,
            '/finance/financing/?tab=flex&'
        );

        navigate(url, { replace: true });
    }
}
